/**
 * Claims on a "No assignment" list, by the biller who last held them (review #398,
 * Shayna 2026-10-14: "flag who the assigned biller was before the list became unassigned
 * so that we can follow up with the biller").
 *
 * The lists are system lists, so these claims are in nobody's open pile and the
 * terminated list never shows them. The builder (`build_bundle.no_assignment`) finds each
 * claim's last month end on a real worklist with a person holding it; the page groups by
 * that person. MEASURE-RULES §12.11.
 */
import { decodeClaims, type OpenClaim } from './terminatedClaims'
import type { NoAssignmentClaims, NoAssignmentHolder, NoAssignmentSection } from './types'
import { dateLong, monthYear } from '@/format'

export interface NoAssignmentClaim extends OpenClaim {
  /** The worklist the biller held it on at `held`'s month end. */
  previousWorklist: string
  /** 'YYYY-MM': the last month end the biller held it. */
  held: string
  /** 'YYYY-MM': the first month end after that, by which it had moved. */
  moved: string
}

export function decodeNoAssignmentClaims(c: NoAssignmentClaims): NoAssignmentClaim[] {
  const at = (k: 'pw' | 'held' | 'mv', i: number) => c.dict[k]?.[c.cols[k]?.[i] ?? -1] ?? ''
  return decodeClaims(c).map((claim, i) => ({
    ...claim,
    previousWorklist: at('pw', i),
    held: at('held', i),
    moved: at('mv', i),
  }))
}

/**
 * "By 14 Oct 2026" for the snapshot's own month (its month end has not happened: the
 * pile is the day it loaded), else "by end of Sep 2026".
 */
export function movedBy(month: string, snapshot: string | null, asOf: string | null): string {
  if (!month) return '—'
  if (snapshot && month === snapshot && asOf) return `by ${dateLong(asOf)}`
  return `by end of ${monthYear(month)}`
}

/** The section's totals over the holders served — what a scoped caller can add up. */
export function holderTotals(holders: readonly NoAssignmentHolder[]) {
  const terminated = holders.filter((h) => h.status === 'terminated')
  return {
    people: holders.length,
    claims: holders.reduce((a, h) => a + h.openClaims, 0),
    terminated: terminated.length,
    terminatedClaims: terminated.reduce((a, h) => a + h.openClaims, 0),
  }
}

/** Whether the bundle carries the org-wide figures (full access) or holders only. */
export const hasOrgTotals = (s: NoAssignmentSection): boolean =>
  typeof s.openClaims === 'number' && s.unheld !== undefined
